import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Search, Zap, ArrowRight, Rocket, Target, BarChart3, Phone, CheckCircle2, XCircle, Clock, Building2, ChevronLeft, ChevronRight, StickyNote, Save, Download, ExternalLink, Loader2 } from 'lucide-react';
import DashboardCharts from '../components/DashboardCharts';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_CONFIG = {
  unprocessed: { label: 'Unbearbeitet', color: 'bg-slate-50 text-slate-500 border border-dashed border-slate-300', icon: Target },
  lead_generated: { label: 'Lead generiert', color: 'bg-slate-100 text-slate-700', icon: Target },
  contacted: { label: 'Kontakt aufgenommen', color: 'bg-emerald-100 text-emerald-700', icon: Phone },
  no_interest: { label: 'Kein Interesse', color: 'bg-red-100 text-red-700', icon: XCircle },
  follow_up: { label: 'Wiedervorlage', color: 'bg-amber-100 text-amber-700', icon: Clock },
};

const LEAD_STATUS_OPTIONS = [
  { value: 'lead_generated', label: 'Lead generiert', color: 'bg-slate-100 text-slate-700' },
  { value: 'contacted', label: 'Kontakt aufgenommen', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'no_interest', label: 'Kein Interesse', color: 'bg-red-100 text-red-700' },
  { value: 'follow_up', label: 'Wiedervorlage', color: 'bg-amber-100 text-amber-700' },
];

export default function DashboardPage() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contactedLeads, setContactedLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0, total_pages: 1 });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteDialog, setNoteDialog] = useState({ open: false, lead: null, note: '' });
  const [savingNote, setSavingNote] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, status: '', label: '', accounts: [], loading: false, exporting: false });

  const STATUS_LABELS = {
    unprocessed: 'Unbearbeitet',
    lead_generated: 'Lead generiert',
    contacted: 'Kontakt aufgenommen',
    no_interest: 'Kein Interesse',
    follow_up: 'Wiedervorlage',
  };

  const openStatusDialog = async (statusKey) => {
    const label = STATUS_LABELS[statusKey] || statusKey;
    setStatusDialog({ open: true, status: statusKey, label, accounts: [], loading: true, exporting: false });
    try {
      const res = await api.get(`/discovery/by-status?status=${encodeURIComponent(statusKey)}`);
      setStatusDialog(d => ({ ...d, accounts: res.data.accounts || [], loading: false }));
    } catch (e) {
      toast.error('Fehler beim Laden der Unternehmen');
      setStatusDialog(d => ({ ...d, loading: false }));
    }
  };

  const exportStatusExcel = async () => {
    if (!statusDialog.status) return;
    setStatusDialog(d => ({ ...d, exporting: true }));
    try {
      const res = await api.get(`/discovery/by-status/export?status=${encodeURIComponent(statusDialog.status)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      const safeLabel = (statusDialog.label || statusDialog.status).replace(/[^\w-]/g, '_');
      link.setAttribute('download', `LaizyLeads_${safeLabel}_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel-Export gestartet');
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setStatusDialog(d => ({ ...d, exporting: false }));
    }
  };

  const loadData = useCallback(async (page = 1) => {
    try {
      const [statsRes, profileRes, membersRes] = await Promise.all([
        api.get(`/discovery/statistics?page=${page}&per_page=10`),
        api.get('/company/profile'),
        api.get('/team/members'),
      ]);
      setStats({
        ...statsRes.data.statistics,
        members: membersRes.data.members?.length || 0,
      });
      setContactedLeads(statsRes.data.contacted_leads || []);
      setPagination(statsRes.data.pagination || { page: 1, per_page: 10, total: 0, total_pages: 1 });
      setProfile(profileRes.data.profile);
    } catch (err) {
      console.error('Dashboard load error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData(1);
  }, [loadData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setLoading(true);
      loadData(newPage);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/discovery/accounts/${leadId}/status`, { status: newStatus });
      setContactedLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, lead_status: newStatus } : lead
      ));
      toast.success('Status aktualisiert');
      // Reload to update statistics
      loadData(pagination.page);
    } catch (err) {
      toast.error('Status konnte nicht aktualisiert werden');
    }
  };

  const openNoteDialog = (lead) => {
    setNoteDialog({ open: true, lead, note: lead.note || '' });
  };

  const saveNote = async () => {
    if (!noteDialog.lead) return;
    setSavingNote(true);
    try {
      await api.put(`/discovery/accounts/${noteDialog.lead.id}/note`, { note: noteDialog.note });
      setContactedLeads(prev => prev.map(lead => 
        lead.id === noteDialog.lead.id ? { ...lead, note: noteDialog.note } : lead
      ));
      toast.success('Notiz gespeichert');
      setNoteDialog({ open: false, lead: null, note: '' });
    } catch (err) {
      toast.error('Notiz konnte nicht gespeichert werden');
    }
    setSavingNote(false);
  };

  if (loading && !stats) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (!company?.onboarding_completed) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="onboarding-prompt">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Rocket className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-3">Willkommen bei LaizyLeads</h1>
        <p className="text-muted-foreground text-lg mb-8">Richten Sie Ihr Firmenprofil ein, um mit der Leadsuche zu beginnen.</p>
        <Button size="lg" onClick={() => navigate('/onboarding')} data-testid="start-onboarding-btn">
          <span className="flex items-center gap-2">Einrichtung starten <ArrowRight className="w-4 h-4" /></span>
        </Button>
      </div>
    );
  }

  const totalLeads = stats?.total || 0;
  const successRate = totalLeads > 0 ? Math.round((stats?.contacted || 0) / totalLeads * 100) : 0;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Übersicht Ihrer Lead-Aktivitäten und Erfolgsquote</p>
      </div>

      {/* Main Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="animate-fade-in-up" data-testid="stat-total-leads">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Leads gesamt</span>
              <Target className="w-5 h-5 text-indigo-500" />
            </div>
            <p className="text-3xl font-bold">{totalLeads}</p>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up" style={{ animationDelay: '0.05s' }} data-testid="stat-contacted">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Kontaktiert</span>
              <Phone className="w-5 h-5 text-emerald-500" />
            </div>
            <p className="text-3xl font-bold text-emerald-600">{stats?.contacted || 0}</p>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up" style={{ animationDelay: '0.1s' }} data-testid="stat-follow-up">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Wiedervorlage</span>
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-3xl font-bold text-amber-600">{stats?.follow_up || 0}</p>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up" style={{ animationDelay: '0.15s' }} data-testid="stat-success-rate">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Erfolgsquote</span>
              <CheckCircle2 className="w-5 h-5 text-violet-500" />
            </div>
            <p className="text-3xl font-bold text-violet-600">{successRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Distribution */}
      <Card className="mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Lead-Status Übersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {Object.entries(STATUS_CONFIG).map(([key, config]) => {
              const count = stats?.[key] || 0;
              const percentage = totalLeads > 0 ? Math.round(count / totalLeads * 100) : 0;
              const Icon = config.icon;
              const disabled = count === 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => !disabled && openStatusDialog(key)}
                  disabled={disabled}
                  className={`p-4 rounded-xl text-left transition-all ${config.color} ${disabled ? 'opacity-60 cursor-default' : 'cursor-pointer hover:scale-[1.03] hover:shadow-md active:scale-[0.98]'}`}
                  data-testid={`status-${key}`}
                  title={disabled ? 'Keine Unternehmen in diesem Status' : `${count} Unternehmen anzeigen`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{config.label}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{count}</span>
                    <span className="text-sm opacity-70">({percentage}%)</span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <DashboardCharts />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contacted Leads List with Pagination */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">
              Bearbeitete Leads ({pagination.total})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/discovery')} data-testid="view-all-leads-btn">
              Alle Leads <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {contactedLeads.length === 0 ? (
              <div className="text-center py-8">
                <Phone className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Noch keine Leads bearbeitet</p>
                <Button size="sm" onClick={() => navigate('/discovery')} data-testid="go-to-leads-btn">
                  Zu den Leads
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Unternehmen</TableHead>
                        <TableHead>Telefon</TableHead>
                        <TableHead className="w-[160px]">Status</TableHead>
                        <TableHead className="w-[80px]">Notiz</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contactedLeads.map(lead => {
                        const statusConfig = STATUS_CONFIG[lead.lead_status] || STATUS_CONFIG.lead_generated;
                        const phoneNumber = lead.contact_info?.general_phone || lead.contact_info?.contact_phone || lead.contact_info?.phone || '';
                        return (
                          <TableRow key={lead.id} data-testid={`contacted-lead-${lead.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(`/discovery/accounts/${lead.id}`)}>
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                  <span className="font-medium text-sm hover:text-primary">{lead.company_name}</span>
                                  {lead.industry && <p className="text-xs text-muted-foreground">{lead.industry}</p>}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {phoneNumber ? (
                                <a href={`tel:${phoneNumber}`} className="text-sm text-primary hover:underline flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <span className="truncate max-w-[120px]">{phoneNumber}</span>
                                </a>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Select 
                                value={lead.lead_status || 'lead_generated'} 
                                onValueChange={(val) => updateLeadStatus(lead.id, val)}
                              >
                                <SelectTrigger className={`h-8 text-xs ${LEAD_STATUS_OPTIONS.find(o => o.value === lead.lead_status)?.color || 'bg-slate-100'}`} data-testid={`status-select-${lead.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {LEAD_STATUS_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      <span className={`px-2 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className={`h-8 w-8 p-0 ${lead.note ? 'text-amber-500' : 'text-muted-foreground'}`}
                                onClick={() => openNoteDialog(lead)}
                                data-testid={`note-btn-${lead.id}`}
                              >
                                <StickyNote className="w-4 h-4" />
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => navigate(`/discovery/accounts/${lead.id}`)}
                              >
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Seite {pagination.page} von {pagination.total_pages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1 || loading}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages || loading}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Company Profile & Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Schnellzugriff</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button className="w-full justify-start" onClick={() => navigate('/discovery')} data-testid="quick-discovery-btn">
              <Search className="w-4 h-4 mr-2" /> Neue Leads finden
            </Button>
            
            {profile && (
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <p className="text-xs text-muted-foreground">Ihr Unternehmen</p>
                <p className="text-sm font-medium">{profile.industry}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{profile.value_proposition}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-2xl font-bold">{stats?.members || 0}</p>
                <p className="text-xs text-muted-foreground">Teammitglieder</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <p className="text-2xl font-bold">{company?.subscription_plan ? company.subscription_plan.charAt(0).toUpperCase() : 'F'}</p>
                <p className="text-xs text-muted-foreground">Plan</p>
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={() => navigate('/settings')} data-testid="quick-settings-btn">
              <Zap className="w-4 h-4 mr-2" /> Einstellungen
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(open) => !open && setNoteDialog({ open: false, lead: null, note: '' })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5" /> Notiz für {noteDialog.lead?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Notizen zu diesem Lead eingeben..."
              value={noteDialog.note}
              onChange={(e) => setNoteDialog(prev => ({ ...prev, note: e.target.value }))}
              rows={5}
              data-testid="note-textarea"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNoteDialog({ open: false, lead: null, note: '' })}>
              Abbrechen
            </Button>
            <Button onClick={saveNote} disabled={savingNote} data-testid="save-note-btn">
              {savingNote ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Speichern...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" /> Speichern
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Detail Dialog */}
      <Dialog open={statusDialog.open} onOpenChange={(o) => setStatusDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="status-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-8">
              <span className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" /> {statusDialog.label}
                <Badge variant="secondary" className="ml-1">{statusDialog.accounts.length}</Badge>
              </span>
              <Button
                size="sm"
                onClick={exportStatusExcel}
                disabled={statusDialog.exporting || statusDialog.accounts.length === 0}
                data-testid="status-export-excel-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {statusDialog.exporting ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Wird erstellt...</span>
                ) : (
                  <span className="flex items-center gap-2"><Download className="w-4 h-4" /> Als Excel exportieren</span>
                )}
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {statusDialog.loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : statusDialog.accounts.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-12">Keine Unternehmen in diesem Status.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unternehmen</TableHead>
                    <TableHead>Branche</TableHead>
                    <TableHead>Standort</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-right">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusDialog.accounts.map(acc => (
                    <TableRow key={acc.id} data-testid={`status-row-${acc.id}`}>
                      <TableCell className="font-medium">{acc.company_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{acc.industry || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{acc.location || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{acc.match_score ?? 0}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/discovery/accounts/${acc.id}`)}
                          data-testid={`status-open-${acc.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Öffnen
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
